class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) form.addEventListener('submit', this.onFormSubmit.bind(this), true);

    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.close.bind(this));

    const clearButton = this.querySelector('button[type="reset"]');
    if (clearButton) clearButton.addEventListener('click', this.clear.bind(this));

    const submitButton = this.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.addEventListener('click', () => {
        if (this.getQuery().length) this.saveSearchTerm(this.getQuery());
      }, true);
    }

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    this.predictiveSearchResults.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      const button = event.target.closest('button');
      if ((link || button) && this.getQuery().length) {
        this.saveSearchTerm(this.getQuery());
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clear();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }
  
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    if (this.getQuery().length) this.saveSearchTerm(this.getQuery());
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showRecentSearches();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.clear(event);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        if (this.getQuery().length) this.saveSearchTerm(this.getQuery());
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
    
    if (event.key === 'Enter' || event.keyCode === 13) {
      if (this.getQuery().length) this.saveSearchTerm(this.getQuery());
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(
  `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}`
  + `&${encodeURIComponent('resources[limit]')}=${perPage}`
  + `&${encodeURIComponent('resources[limit_scope]')}=each`
  + `&resources[type]=product`
  + `&resources[options][unavailable_products]=hide`
  + `&resources[options][fields]=title,vendor,product_type,tag,variants.title,variants.sku,variants.barcode`
  + `&section_id=predictive-search`
)
.then((response) => {
  if (!response.ok) {
    var error = new Error(response.status);
    this.close();
    throw error;
  }
  return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.prioritizeSkuMatches();
    this.maybeFetchSkuFallback(this.searchTerm);
    this.setLiveRegionResults();
    this.open();
  }

  // Shopify's predictive search API fuzzy-matches SKUs, so a query like
  // "knkn6317" can rank a similar-but-wrong SKU above the exact one.
  // Re-sort the fetched products so exact/prefix SKU matches come first.
  prioritizeSkuMatches() {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return;

    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const items = Array.from(list.children);

    const scoreOf = (item) => {
      const skus = (item.dataset.sku || '').split(',').filter(Boolean);
      if (skus.some((sku) => sku === term)) return 0;
      if (skus.some((sku) => sku.startsWith(term))) return 1;
      return 2;
    };

    const scored = items.map((item, index) => ({ item, index, score: scoreOf(item) }));
    if (!scored.some(({ score }) => score < 2)) return;

    scored
      .sort((a, b) => a.score - b.score || a.index - b.index)
      .forEach(({ item }) => list.appendChild(item));
  }

  // Shopify's predictive search endpoint sometimes omits the correct product
  // entirely for a SKU query (not just ranks it low) even though the normal
  // /search page finds it fine. When the query looks like a SKU and none of
  // the fetched products match it, fall back to a section-rendered /search
  // request (the same engine Enter uses) and splice the real match in.
  maybeFetchSkuFallback(rawTerm) {
    const term = rawTerm.trim();
    const lowerTerm = term.toLowerCase();
    const looksLikeSku = /^[a-z0-9]+$/i.test(term) && /[a-z]/i.test(term) && /[0-9]/.test(term) && term.length >= 5;
    if (!looksLikeSku) return;

    const list = this.querySelector('#predictive-search-results-products-list');
    const alreadyMatched = list && Array.from(list.children).some((item) => {
      const skus = (item.dataset.sku || '').split(',');
      return skus.includes(lowerTerm) || item.textContent.toLowerCase().includes(lowerTerm);
    });
    if (alreadyMatched) return;

    const cacheKey = `sku-fallback:${lowerTerm}`;
    if (cacheKey in this.cachedResults) {
      this.injectSkuFallbackResult(this.cachedResults[cacheKey], lowerTerm);
      return;
    }

    fetch(`${theme.routes.search_url}?q=${encodeURIComponent(term)}&type=product&options[prefix]=last&section_id=main-search`)
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const cards = Array.from(doc.querySelectorAll('#product-grid .card-wrapper'));
        const match = cards.find((card) => {
          const heading = card.querySelector('.card-information__text');
          return heading && heading.textContent.toLowerCase().includes(lowerTerm);
        });

        let result = null;
        if (match) {
          const heading = match.querySelector('.card-information__text');
          const link = match.querySelector('a.full-unstyled-link') || match.querySelector('a[href]');
          const img = match.querySelector('img');
          result = {
            title: heading.textContent.trim(),
            url: link ? link.getAttribute('href') : '',
            imgSrc: img ? (img.getAttribute('src') || '') : '',
          };
        }

        this.cachedResults[cacheKey] = result;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[cacheKey] = result;
        });
        this.injectSkuFallbackResult(result, lowerTerm);
      })
      .catch(() => {});
  }

  injectSkuFallbackResult(result, lowerTerm) {
    if (!result || !result.url) return;
    if (this.getQuery().trim().toLowerCase() !== lowerTerm) return; // user moved on

    let list = this.querySelector('#predictive-search-results-products-list');
    if (!list) list = this.buildEmptyProductsGroup();
    if (!list) return;

    if (Array.from(list.children).some((item) => item.dataset.fallbackUrl === result.url)) return;

    const li = document.createElement('li');
    li.className = 'predictive-search__list-item';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.dataset.sku = lowerTerm;
    li.dataset.fallbackUrl = result.url;

    const titleSpan = document.createElement('span');
    titleSpan.className = 'predictive-search__item-heading h4';
    titleSpan.textContent = result.title;

    const content = document.createElement('div');
    content.className = 'predictive-search__item-content';
    content.appendChild(titleSpan);

    const link = document.createElement('a');
    link.href = result.url;
    link.className = 'predictive-search__item predictive-search__item--link link link--text';
    link.tabIndex = -1;

    if (result.imgSrc) {
      const imageWrap = document.createElement('div');
      imageWrap.innerHTML = `
        <span class="predictive-search__image media-wrapper media-wrapper--small">
          <div class="media media--adapt" style="--image-ratio-percent: 100%;">
            <img src="${result.imgSrc}" alt="" width="70" height="70" loading="lazy">
          </div>
        </span>
      `;
      link.appendChild(imageWrap);
    }

    link.appendChild(content);
    li.appendChild(link);
    list.insertBefore(li, list.firstChild);

    const perPage = Number(this.dataset.perPage) || 4;
    Array.from(list.children).slice(perPage).forEach((item) => item.remove());

    this.querySelector('.predictive-search-results')?.classList.remove('is-empty');
  }

  // Builds the products heading + list when predictive search matched zero
  // products at all, so the SKU fallback result still has somewhere to render.
  buildEmptyProductsGroup() {
    const resultsRoot = this.querySelector('#predictive-search__results-list');
    if (!resultsRoot) return null;

    resultsRoot.classList.remove('is-empty');
    resultsRoot.querySelector('.title-wrapper.center')?.remove();

    let wrapper = resultsRoot.querySelector('#predictive-search-results-groups-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.id = 'predictive-search-results-groups-wrapper';
      wrapper.className = 'predictive-search__results-groups-wrapper';
      const searchForButton = resultsRoot.querySelector('#predictive-search-option-search-keywords');
      resultsRoot.insertBefore(wrapper, searchForButton || null);
    }

    let group = wrapper.querySelector('.predictive-search__result-group:last-of-type');
    if (!group) {
      group = document.createElement('div');
      group.className = 'predictive-search__result-group';
      wrapper.appendChild(group);
    }

    const container = document.createElement('div');
    const heading = document.createElement('h2');
    heading.id = 'predictive-search-products';
    heading.className = 'predictive-search__heading h4';
    heading.textContent = this.getAttribute('data-products-heading') || 'Products';
    container.appendChild(heading);

    const ul = document.createElement('ul');
    ul.id = 'predictive-search-results-products-list';
    ul.className = 'predictive-search__results-list list-unstyled';
    ul.setAttribute('role', 'group');
    ul.setAttribute('aria-labelledby', 'predictive-search-products');
    container.appendChild(ul);

    group.insertBefore(container, group.firstChild);

    return ul;
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    document.body.classList.remove('predictive-search--focus');
    if (this.modal) this.modal.classList.remove('searching');
  }

  clear(event) {
    if (event) event.preventDefault();
    
    this.input.value = '';
    this.input.focus();

    if (this.modal) this.modal.classList.remove('searching');
    
    if (!this.showRecentSearches()) {
      this.removeAttribute('open');
      this.removeAttribute('results');
    }
  }

  saveSearchTerm(term) {
    if (!term) return;
    let searches = [];
    try {
      searches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
    } catch (e) {
      searches = [];
    }
    searches = searches.filter(t => t.toLowerCase() !== term.toLowerCase());
    searches.unshift(term);
    if (searches.length > 5) searches.pop();
    localStorage.setItem('recent_searches', JSON.stringify(searches));
  }

  showRecentSearches() {
    let recentSearches = [];
    try {
      recentSearches = JSON.parse(localStorage.getItem('recent_searches') || '[]');
    } catch (e) {
      recentSearches = [];
    }

    // Build the recent searches panel inside predictiveSearchResults
    let html = `
      <div class="global-recent-searches-wrapper" style="display:block;position:static;box-shadow:none;border-radius:0;margin:0;padding:1.5rem 0;" role="listbox">
        <div class="recent-searches-top" style="display:flex;align-items:center;justify-content:space-between;">
          <h2 class="recent-searches-header">Recent Searches</h2>
          ${recentSearches.length > 0 ? `<button type="button" class="recent-searches-clear" style="background:none;border:none;color:currentColor;cursor:pointer;font-size:13px;text-decoration:underline;opacity:.7;">Clear all</button>` : ''}
        </div>
        <ul class="recent-searches-list" role="group">
    `;

    if (recentSearches.length === 0) {
      html += `
          <li class="recent-searches-item" style="padding: 15px 16px; color: #888; font-size: 14px; text-align: center; font-style: italic;">
            No recent searches
          </li>
      `;
    } else {
      recentSearches.forEach((term) => {
        html += `
            <li class="recent-searches-item" role="option">
              <a href="${window.theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(term)}" class="recent-searches-link" tabindex="-1">
                <svg class="recent-searches-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                <span>${term}</span>
              </a>
            </li>
        `;
      });
    }

    html += `
        </ul>
      </div>
    `;

    this.predictiveSearchResults.innerHTML = html;
    this.setAttribute('results', true);
    if (this.modal) this.modal.classList.add('searching');
    this.open();

    const clearBtn = this.predictiveSearchResults.querySelector('.recent-searches-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        localStorage.removeItem('recent_searches');
        this.showRecentSearches();
      });
    }

    return true;
  }
}

customElements.define('predictive-search', PredictiveSearch);
